import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useRecentSplit } from '@tunasong/search'
import { Typography, useFavorites } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { CollaboratorRow } from './collaborator-row.js'
import { useIsSoloUser } from './use-is-solo-user.js'
import styles from './collaborators-section.module.css'

/** Rows the aside shows */
const collaboratorsCap = 5

/**
 * Home aside: what the people I share spaces with changed, as compact "{Name} edited {Title}" rows.
 *
 * Source: `useRecentSplit().theirs`. Entries without `lastEditedBy` (indexed before the field existed) are
 * skipped, since nothing says who edited them; pinned items are skipped because Continue already shows them.
 * Hidden entirely for a solo user, and whenever there is nothing to show.
 */
export const CollaboratorsSection: FC = () => {
  const { theirs } = useRecentSplit()
  const { isPinned } = useFavorites()
  const isSolo = useIsSoloUser()

  const editorById = new Map(
    theirs.flatMap(e => (e.lastEditedBy && !isPinned(e.id) ? [[e.id, e.lastEditedBy] as const] : []))
  )
  const ids = [...editorById.keys()]
  const { existingEntities = [] } = graphHooks.useEntitiesById(ids)
  const entitiesById = new Map(existingEntities.map(e => [e.id, e]))

  const rows = ids
    .map(id => entitiesById.get(id))
    .filter((e): e is Persisted<Entity> => Boolean(e) && !e?.trash)
    .slice(0, collaboratorsCap)

  if (isSolo || rows.length === 0) {
    return null
  }

  return (
    <section className={styles.section} aria-labelledby="home-collaborators">
      <Typography id="home-collaborators" variant="h2" className={styles.heading}>
        From collaborators
      </Typography>
      <ul className={styles.list}>
        {rows.map(entity => (
          <CollaboratorRow key={entity.id} entity={entity} editorId={editorById.get(entity.id) ?? entity.userId} />
        ))}
      </ul>
    </section>
  )
}

export default CollaboratorsSection
